import { Injectable } from '@angular/core';
import { first, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AppState } from '@api-ui-app/src/app/app.state';
import { getStore } from '@api-ui-app/src/app/ngrx/e-commerce-stores.selector';
import { EStore } from '@gelato-api-ui/core/e-commerce/e-store';
import { logEvent, logEventV2 } from '@gelato-api-ui/core/analytics/helpers/trackEvent';
import { EProductWithVariants } from '@gelato-api-ui/core/e-commerce/e-product-with-variants';
import { getSanityProductIdFromEProduct } from '@gelato-api-ui/core/e-commerce/helpers/getSanityProductIdFromEProduct';
import { EProductPublishMode } from '@api-ui-app/src/app/e-commerce-product-publishing/types/e-product-publish-mode.enum';
import { GelatoPublisherEventName } from '@api-ui-app/src/app/e-commerce-product-expansion/types/gelato-publisher-event-name.enum';
import { EProductMetadataKey } from '@gelato-api-ui/core/e-commerce/e-product-metadata-key.enum';
import { EProductPublishScope } from '@gelato-api-ui/core/e-commerce/e-product-publish-scope.enum';
import { ECommerceProductPublishSettingsService } from '@api-ui-app/src/app/e-commerce-product-publishing/services/e-commerce-product-publish-settings.service';
import { MetadataService } from '@gelato-api-ui/core/metadata/services/metadata.service';
import { MetadataItem } from '@gelato-api-ui/core/metadata/types/metadata-item';

@Injectable({ providedIn: 'root' })
export class ECommerceProductPublishingEventTrackingService {
  private readonly selectedStore$ = this.store.select(getStore);
  private productMetadata: MetadataItem[];

  constructor(
    private readonly store: Store<AppState>,
    private readonly eCommerceProductPublishSettingsService: ECommerceProductPublishSettingsService,
    private readonly metadataService: MetadataService,
  ) {}

  setProductMetadata(productMetadata: MetadataItem[]) {
    this.productMetadata = productMetadata;
  }

  trackPublishSuccess(product: EProductWithVariants) {
    const eventProperties = {
      storeProductId: product.id,
      productName: getSanityProductIdFromEProduct(product),
      scopes: this.scopes,
      publishMode: this.publishMode,
      mode: undefined,
    };

    const eventTypeConfig = {
      [EProductPublishMode.PRODUCT_EXPANSION]: GelatoPublisherEventName.PRODUCT_PUBLISHED_SUCCESSFUL,
    };

    if (this.publishMode === EProductPublishMode.PRODUCT_EXPANSION) {
      eventProperties.mode = this.metadataService.getItemValue(
        product?.metadata,
        EProductMetadataKey.GELATO_PUBLISHER_PRODUCT_PUBLISH_MODE,
      ) as string;
    }

    if (eventTypeConfig[this.publishMode]) {
      this.trackEvent(eventTypeConfig[this.publishMode], eventProperties, true);
    } else {
      this.trackEvent('storeProductPublishSuccess', eventProperties, false);
    }
  }

  trackPublishError(productId: string, error: any) {
    const eventTypeConfig = {
      [EProductPublishMode.PRODUCT_EXPANSION]: GelatoPublisherEventName.PRODUCT_PUBLISHED_FAILED,
    };

    const eventProperties = {
      storeProductId: productId,
      error,
      scopes: this.scopes,
      publishMode: this.publishMode,
    };

    if (eventTypeConfig[this.publishMode]) {
      this.trackEvent(eventTypeConfig[this.publishMode], eventProperties, true);
    } else {
      this.trackEvent('storeProductPublishFailed', eventProperties, false);
    }
  }

  private trackEvent(eventType: string, eventProperties: object, useEventVersionV2: boolean = false) {
    this.selectedStore$
      .pipe(
        first(),
        tap((selectedStore: EStore) => {
          const eventPropertiesEnhanced = {
            storeType: selectedStore?.type,
            storeName: selectedStore?.name,
            storeId: selectedStore?.id,
            scopes: this.scopes,
            ...eventProperties,
          };

          const logEventFunc = useEventVersionV2 ? logEventV2 : logEvent;

          logEventFunc(eventType, eventPropertiesEnhanced);
        }),
      )
      .subscribe();
  }

  private get scopes(): EProductPublishScope[] {
    return this.eCommerceProductPublishSettingsService.getPublishScopesFromMetadata(this.productMetadata);
  }

  private get publishMode(): EProductPublishMode {
    return this.eCommerceProductPublishSettingsService.getPublishModeFromMetadata(this.productMetadata);
  }
}
